import type { InventoryItem } from '../redux/inventorySlice';
import { canonicalDishTypes, fold } from '../utils/dishTypeVocabulary';
import { detectProtein, withoutSeafoodSeasoning } from '../utils/dietClassifier';
import { hasIngredients } from '../utils/recipeContent';
import { entryFor, type IngredientCategory } from '../utils/ingredientVocabulary';
import { addDays } from '../utils/localDate';
import { PERISHABLE_CATEGORIES } from './pantryFirst';
import { activeRows, resolveLine, shelfMatcher } from './pantryMatch';

/**
 * Accompaniments (#746): what goes beside a dish that is already on the menu.
 *
 * « Ajouter un plat » used to open the same fan as « Remplacer », which ranks
 * dinners — so the idea offered next to a chicken curry was a second dinner.
 * This is the other list: side dishes and salads only, never a second plate of
 * meat or fish, picked for the dish they will sit next to.
 */

/** The fields eligibility and ranking read; both recipe index shapes satisfy it. */
export interface SideRecipe {
  id: string;
  title: string;
  readyInMinutes?: number;
  dishTypes?: string[];
  ingredients: string[];
  translatedTitles?: Record<string, string>;
  sourceRating?: number;
}

const MEAT_OR_FISH = new Set(['chicken', 'beef', 'pork', 'fish', 'seafood', 'lamb', 'turkey']);

/** Labels that say the recipe is something other than a side, whatever else it carries. */
const NOT_A_SIDE = ['main course', 'dessert', 'beverage', 'sauce', 'breakfast', 'soup'] as const;

/**
 * Animal protein `detectProtein` has no keyword for. A duck confit salad read
 * as protein-free, and cured fish is a plate of fish whatever the label says.
 */
const OTHER_MEAT_RE = /\b(?:canards?|duck|foie gras|gravlax|tartares?|ceviche|carpaccio|pancetta|prosciutto|lardons?|guanciale|chorizo|merguez)\b/i;

/**
 * Stock is a cooking liquid, not the protein on the plate: « Bok choy au
 * sésame » simmers in chicken broth and is still a vegetable. Taken out of a
 * line before it is read for meat. (A declared vegetarian is still protected:
 * the cook's hard rules read the whole recipe and run on top of this.)
 */
const STOCK_RE = /\b(?:bouillons?|fonds?|fumets?|consomm[ée]s?)\s+(?:de |d['’]|du |des )?(?:poulet|volaille|dinde|b[oœ]?e?uf|boeuf|veau|porc|l[ée]gumes)\b|\b(?:chicken|beef|turkey|veal|pork)\s+(?:stock|broth|bouillon|bone broth)\b/gi;

/**
 * A title that names something else. The label is a model's answer and a few
 * are wrong: two soups, a pizza dough and a salsa were filed as side dishes.
 */
const NOT_A_SIDE_TITLE_RE = /\b(?:potages?|soupes?|soups?|veloutes?|bouillons?|broth|dough|salsas?|salade-repas)\b/;

const meatIn = (title: string, ingredients: string[]): boolean => {
  const protein = detectProtein({ title, ingredients });
  return (protein !== null && MEAT_OR_FISH.has(protein)) || OTHER_MEAT_RE.test([title, ...ingredients].join(' '));
};

const withoutStock = (line: string): string => withoutSeafoodSeasoning(line).replace(STOCK_RE, ' ');

/** Every name the card can show, so a French title is read even on an English original. */
const titlesOf = (recipe: SideRecipe): string =>
  [recipe.title, ...Object.values(recipe.translatedTitles ?? {})].join(' · ');

const accompanimentCache = new WeakMap<SideRecipe, boolean>();

/**
 * Is this recipe something served beside a main dish?
 *
 * - labelled `side dish` or `salad`, and nothing that says otherwise — a salad
 *   also labelled `main course` is a dinner;
 * - no meat or fish named in a title;
 * - no meat or fish in the ingredient list, stock aside.
 *
 * Legumes, eggs and cheese are allowed: a chickpea salad is a side. The label
 * is required, never guessed from the title — an unlabelled recipe is a dinner
 * until the nightly dish-type sweep says otherwise.
 */
export function isAccompaniment(recipe: SideRecipe): boolean {
  const cached = accompanimentCache.get(recipe);
  if (cached !== undefined) return cached;
  const answer = computeIsAccompaniment(recipe);
  accompanimentCache.set(recipe, answer);
  return answer;
}

function computeIsAccompaniment(recipe: SideRecipe): boolean {
  if (!hasIngredients(recipe)) return false;
  const types = canonicalDishTypes(recipe.dishTypes) as string[];
  if (!types.includes('side dish') && !types.includes('salad')) return false;
  if (NOT_A_SIDE.some((t) => types.includes(t))) return false;
  if (NOT_A_SIDE_TITLE_RE.test(fold(titlesOf(recipe)))) return false;
  if (meatIn(titlesOf(recipe), [])) return false;
  return !meatIn('', recipe.ingredients.map(withoutStock));
}

export type SideKind = 'starch' | 'salad' | 'vegetable';

const STARCH_RE = /^(?:frites|fries)\b|\b(?:pommes? de terre|patat[ae]s?|potato(?:es)?|pommes frites|french fries|tostones|riz|rice|quinoa|couscous|orzo|boulgour|bulgur|polenta|gnocchis?|pates|pasta|nouilles|noodles?|raviolis?|pains?|bread|naan|focaccia|tortillas?)\b/;
const SALAD_RE = /\b(?:salades?|salad|slaw|panzanella|fattouche|taboule|tabbouleh)\b/;

/** What a side brings to the plate. Read off the titles: that is where a side names itself. */
export function sideKindOf(recipe: SideRecipe): SideKind {
  const text = fold(titlesOf(recipe));
  if (STARCH_RE.test(text)) return 'starch';
  if (SALAD_RE.test(text)) return 'salad';
  return 'vegetable';
}

const MAIN_STARCH_RE = /\b(?:pommes? de terre|patates?|potato(?:es)?|frites|fries|riz|rice|risotto|quinoa|couscous|semoule|orzo|boulgour|bulgur|polenta|gnocchis?|pates|pasta|spaghettis?|penne|linguines?|fettuccine|macaronis?|lasagn\w*|nouilles|noodles?|ramen|udon|vermicelles?|raviolis?|pains?|bread|buns?|burgers?|sandwich\w*|wraps?|naan|pitas?|tortillas?|tacos?|pizzas?|orge|barley|farro)\b/;

/** Does the main already carry its starch? Then the plate wants something fresh, not more of it. */
export function mainHasStarch(main: SideRecipe): boolean {
  return MAIN_STARCH_RE.test(fold([titlesOf(main), ...main.ingredients].join(' ')));
}

export type SideAngleKey = 'fridge' | 'together' | 'balance' | 'new';

/** Display order, and the order the hero's single suggestion is taken in. */
export const SIDE_ANGLES: SideAngleKey[] = ['fridge', 'together', 'balance', 'new'];

export interface SideSuggestion<T extends SideRecipe = SideRecipe> {
  angleKey: SideAngleKey;
  recipe: T;
  /** The reason line, rendered by the caller with `t(reasonKey, reasonParams)`. */
  reasonKey: string;
  reasonParams?: Record<string, string | number>;
  /**
   * The fridge angle's foods, most worth using first (canonical ingredient
   * ids). `sideReasons.fridgeFoods` wants them as `{{foods}}`, which the caller
   * writes in the reader's language.
   */
  foods?: string[];
}

export interface SideSuggestionsParams<T extends SideRecipe = SideRecipe> {
  /** The dish the side will sit next to. */
  main: SideRecipe;
  allRecipes: T[];
  /** Recipes already in this slot — never offered again beside themselves. */
  slotRecipeIds?: string[];
  /** Recipes placed anywhere this week; avoided while the pool allows. */
  placedRecipeIds?: string[];
  /** Active inventory items. */
  inventory: InventoryItem[];
  /** The cook's hard rules (allergies, exclusions, declared diets). Never widened. */
  exclude?: (recipe: T) => boolean;
  /** Per-recipe signals, keyed by recipe id. */
  metadataById?: Record<string, { rating?: number; lastMade?: string }>;
  /** A new seed gives fresh picks; the same seed gives the same list. */
  seed: number;
  /** Today (YYYY-MM-DD), for what runs out soon. Without it no food counts as expiring. */
  today?: string;
}

/** A food that runs out within this many days is worth one more. */
const EXPIRING_WITHIN_DAYS = 3;
/** What a jar, a can or a spice is worth beside a fresh food: it uses nothing up. */
const SHELF_STABLE_WEIGHT = 0.25;
/** Each row of a food past the first: the cook has more of it than one dish will use. */
const EXTRA_ROW_WEIGHT = 2;
/** Past three rows of one food, more rows say nothing new. */
const MAX_ROWS = 3;
/** How many foods the fridge reason names. */
const NAMED_FOODS = 2;
/** A fridge side scoring at least this share of the best one may be picked in its place. */
const NEAR_BEST = 0.75;

/** A food on the shelf as the fridge angle weighs it. */
interface ShelfFood {
  /** The canonical ingredient, or the row's own name when the vocabulary does not know it. */
  key: string;
  canonicalId: string | null;
  /** Fresh enough to spoil: the only foods the reason names. */
  fresh: boolean;
  /** Rows of it filed as that food, in the fridge or on the counter. */
  freshRows: number;
  weight: number;
}

/**
 * What a fresh pantry category can hold. A row's name resolves by any food word
 * in it, so « Thon citron poivre » reads as a lemon and « Raspberry kombucha »
 * as raspberries: such a row is still used by the recipes that match it, but
 * says nothing about how many lemons are in the fridge.
 */
const HOLDS: Record<string, readonly IngredientCategory[]> = {
  Vegetables: ['vegetable', 'fruit', 'aromatic', 'herb'],
  Fruits: ['vegetable', 'fruit', 'aromatic', 'herb'],
  Dairy: ['dairy'],
  Meat: ['protein'],
  'Plant-Based Protein': ['protein'],
  'Bread/Grains': ['grain'],
};

/** Is this row a fresh one of that food: perishable, in the fridge or on the counter, and filed as what it is? */
const isFreshRowOf = (row: InventoryItem, canonicalId: string | null): boolean => {
  if (!PERISHABLE_CATEGORIES.has(row.category) || row.location === 'pantry' || row.location === 'freezer') return false;
  const kind = entryFor(canonicalId)?.category;
  return !kind || (HOLDS[row.category] ?? []).includes(kind);
};

const expiryIso = (item: InventoryItem): string | null => {
  const e = item.expiryDate;
  return e && Number.isInteger(e.year) && Number.isInteger(e.month) && Number.isInteger(e.day)
    ? `${e.year}-${String(e.month).padStart(2, '0')}-${String(e.day).padStart(2, '0')}`
    : null;
};

/**
 * What each shelf row is worth to « Utilise votre frigo » (#853). Counting rows
 * made a lemon worth as much as three heads of lettuce, and a corn salad that
 * touched seven things beat the fattouche built on the cucumbers the cook had
 * plenty of. So rows are grouped by food. A fresh food is worth one, and two
 * more for each further row (three rows of cucumbers, five): having a lot of
 * something is what the cook wants used, and no single recipe line shows it.
 * Only a row filed as that food counts towards it (`isFreshRowOf`). A jar or a
 * spice is worth a quarter, and a food that runs out within three days one more.
 */
function weighShelf(items: readonly InventoryItem[], today: string | undefined): Map<InventoryItem, ShelfFood> {
  const soon = today ? addDays(today, EXPIRING_WITHIN_DAYS) : null;
  const groups = new Map<string, { canonicalId: string | null; rows: InventoryItem[] }>();
  for (const item of items) {
    const canonicalId = item.canonicalId ?? resolveLine(item.name).canonicalId ?? null;
    const key = canonicalId ?? fold(item.name.trim());
    const group = groups.get(key) ?? { canonicalId, rows: [] };
    group.rows.push(item);
    groups.set(key, group);
  }
  const out = new Map<InventoryItem, ShelfFood>();
  for (const [key, { canonicalId, rows }] of groups) {
    const freshRows = rows.filter((r) => isFreshRowOf(r, canonicalId)).length;
    const fresh = freshRows > 0;
    const expiring = soon !== null && rows.some((r) => {
      const on = expiryIso(r);
      return on !== null && on <= soon;
    });
    const plenty = 1 + EXTRA_ROW_WEIGHT * (Math.min(Math.max(freshRows, 1), MAX_ROWS) - 1);
    const weight = (fresh ? 1 : SHELF_STABLE_WEIGHT) * plenty + (expiring ? 1 : 0);
    const food = { key, canonicalId, fresh, freshRows, weight };
    for (const row of rows) out.set(row, food);
  }
  return out;
}

/** A side that takes longer than this is a project, not an accompaniment. */
const MAX_SIDE_MINUTES = 120;
/** With no cook time on the main, "ready together" means a quick side. */
const DEFAULT_TOGETHER_MINUTES = 30;
/** Below this the pool reaches back into what the week already holds. */
const MIN_POOL = 4;
/** How deep in a ranked angle the seeded pick can reach. */
const TOP_K = 8;

// mulberry32 — the same tiny PRNG the dinner fan shuffles with.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const poolCache = new WeakMap<readonly SideRecipe[], SideRecipe[]>();

/** Every accompaniment of a library. Kept per array: the planner asks on every hover. */
export function accompanimentsOf<T extends SideRecipe>(allRecipes: readonly T[]): T[] {
  let pool = poolCache.get(allRecipes) as T[] | undefined;
  if (!pool) {
    pool = allRecipes.filter(isAccompaniment);
    poolCache.set(allRecipes, pool);
  }
  return pool;
}

const timeOf = (r: SideRecipe): number | null => (r.readyInMinutes && r.readyInMinutes > 0 ? r.readyInMinutes : null);

/**
 * The sides a dish may be offered: accompaniments the cook's hard rules allow,
 * not already beside it, not a project. `fitting` also drops a second starch
 * beside a main that has one — a potato gratin next to a plate of pasta.
 */
function sidePools<T extends SideRecipe>(params: SideSuggestionsParams<T>): { allowed: T[]; fitting: T[]; starchyMain: boolean } {
  const { main, allRecipes, slotRecipeIds = [], exclude } = params;
  const inSlot = new Set([main.id, ...slotRecipeIds]);
  const allowed = accompanimentsOf(allRecipes).filter((r) =>
    !inSlot.has(r.id) && !(exclude && exclude(r)) && (timeOf(r) ?? 0) <= MAX_SIDE_MINUTES);
  const starchyMain = mainHasStarch(main);
  const fitting = starchyMain ? allowed.filter((r) => sideKindOf(r) !== 'starch') : allowed;
  return { allowed, fitting, starchyMain };
}

/** What one recipe takes off the shelf, by food (`weighShelf`). */
interface FridgeUse {
  /** Shelf rows its lines match. */
  rows: number;
  /** The sum of the weights of the foods it uses. */
  score: number;
  /** The foods it uses, most worth using first. */
  foods: ShelfFood[];
}

/** A reader of what each recipe uses, for one shelf. Answers are kept per recipe. */
function fridgeUses<T extends SideRecipe>(inventory: readonly InventoryItem[], today: string | undefined): (r: T) => FridgeUse {
  // The shelf and its matcher are kept per inventory array (#865): the hero
  // asks on every render and the list on every opening.
  const shelf = activeRows(inventory);
  const matcher = shelfMatcher(shelf);
  const weights = shelf.length > 0 ? weighShelf(shelf, today) : new Map<InventoryItem, ShelfFood>();
  const uses = new Map<string, FridgeUse>();
  return (r: T): FridgeUse => {
    let value = uses.get(r.id);
    if (value === undefined) {
      const used = matcher.itemsUsedBy(r);
      // One food counts once, however many of its rows the lines touch.
      const foods = [...new Set(used.map((item) => weights.get(item)).filter((f): f is ShelfFood => !!f))]
        .sort((a, b) => b.weight - a.weight);
      value = { rows: used.length, score: foods.reduce((sum, f) => sum + f.weight, 0), foods };
      uses.set(r.id, value);
    }
    return value;
  };
}

/** A fresh food the cook can ask the list to use (#853). */
export interface SideFood {
  /** Canonical ingredient id. */
  id: string;
  /** Fresh rows of it on the shelf. */
  rows: number;
}

/** How many foods the list offers to pick from. */
const MAX_SIDE_FOODS = 8;
/** How many sides the list shows once foods are picked. */
const MAX_FOOD_MATCHES = 6;

/**
 * The fresh foods on the shelf that at least one side for `main` uses, the
 * ones most worth using first (`weighShelf`): « J'aurais voulu pouvoir
 * spécifier que j'ai bcp de concombre ». A food no side uses is never offered,
 * so a picked food always finds something.
 */
export function sideFoodChoices<T extends SideRecipe = SideRecipe>(params: SideSuggestionsParams<T>): SideFood[] {
  const { fitting } = sidePools(params);
  const useOf = fridgeUses<T>(params.inventory, params.today);
  const best = new Map<string, ShelfFood>();
  for (const recipe of fitting) {
    for (const food of useOf(recipe).foods) {
      if (food.fresh && food.canonicalId !== null) best.set(food.canonicalId, food);
    }
  }
  return [...best.values()]
    .sort((a, b) => b.weight - a.weight || b.freshRows - a.freshRows || a.key.localeCompare(b.key))
    .slice(0, MAX_SIDE_FOODS)
    .map((f) => ({ id: f.canonicalId!, rows: f.freshRows }));
}

/** A side that uses foods the cook picked. */
export interface SideFoodMatch<T extends SideRecipe = SideRecipe> {
  recipe: T;
  /** The picked foods it uses, in the order they were picked. */
  foods: string[];
  /** Up to two other fresh foods from the shelf it uses. */
  also: string[];
}

/**
 * The sides that use the foods the cook picked: every one using all of them
 * first, then those using some, each group by how much of the shelf it uses.
 * `together` is false when no side uses all of them, and the list then holds
 * the ones using some — never an empty list for a food the shelf offered.
 * The hard rules and the second-starch rule apply as in the four angles.
 */
export function buildSidesWithFoods<T extends SideRecipe = SideRecipe>(
  params: SideSuggestionsParams<T> & { foods: readonly string[] },
): { matches: SideFoodMatch<T>[]; together: boolean } {
  const picked = params.foods;
  if (picked.length === 0) return { matches: [], together: true };
  const { fitting } = sidePools(params);
  const useOf = fridgeUses<T>(params.inventory, params.today);
  const scored = fitting.flatMap((recipe) => {
    const use = useOf(recipe);
    const ids = new Set(use.foods.map((f) => f.canonicalId));
    const foods = picked.filter((id) => ids.has(id));
    if (foods.length === 0) return [];
    const also = use.foods
      .filter((f) => f.fresh && f.canonicalId !== null && !picked.includes(f.canonicalId))
      .slice(0, NAMED_FOODS)
      .map((f) => f.canonicalId!);
    return [{ recipe, foods, also, score: use.score }];
  });
  scored.sort((a, b) => b.foods.length - a.foods.length || b.score - a.score
    || (b.recipe.sourceRating ?? 0) - (a.recipe.sourceRating ?? 0) || a.recipe.id.localeCompare(b.recipe.id));
  return {
    matches: scored.slice(0, MAX_FOOD_MATCHES).map(({ recipe, foods, also }) => ({ recipe, foods, also })),
    together: scored.some((s) => s.foods.length === picked.length),
  };
}

/**
 * Up to four accompaniments for `main`, one per angle, each with an honest
 * reason: it uses the fridge, it is ready when the main is, it brings what the
 * plate lacks, or the cook has never made it. An angle with nothing to say is
 * left out rather than padded.
 */
export function buildSideSuggestions<T extends SideRecipe = SideRecipe>(
  params: SideSuggestionsParams<T>,
): SideSuggestion<T>[] {
  const { main, placedRecipeIds = [], inventory, metadataById = {}, seed, today } = params;

  const placed = new Set(placedRecipeIds);
  const { allowed, fitting, starchyMain } = sidePools(params);
  let pool = fitting.filter((r) => !placed.has(r.id));
  if (pool.length < MIN_POOL) pool = fitting;
  if (pool.length < MIN_POOL) pool = allowed;
  if (pool.length === 0) return [];

  const useOf = fridgeUses<T>(inventory, today);
  const hitsOf = (r: T): number => useOf(r).rows;
  const namedFoods = (r: T): string[] => useOf(r).foods
    .filter((f) => f.fresh && f.canonicalId !== null)
    .slice(0, NAMED_FOODS)
    .map((f) => f.canonicalId!);

  const mainMinutes = main.readyInMinutes && main.readyInMinutes > 0 ? main.readyInMinutes : DEFAULT_TOGETHER_MINUTES;
  const togetherCap = Math.min(MAX_SIDE_MINUTES, Math.max(mainMinutes, DEFAULT_TOGETHER_MINUTES));
  const wanted: SideKind[] = starchyMain ? ['salad', 'vegetable'] : ['starch', 'vegetable', 'salad'];
  const balanceKind = wanted.find((kind) => pool.some((r) => sideKindOf(r) === kind));
  const wasTried = (r: T) => metadataById[r.id]?.rating != null || metadataById[r.id]?.lastMade != null;
  const byTime = (a: T, b: T) => (timeOf(a) ?? 999) - (timeOf(b) ?? 999);
  const byRating = (a: T, b: T) => (b.sourceRating ?? 0) - (a.sourceRating ?? 0);

  interface Angle {
    key: SideAngleKey;
    pred: (r: T) => boolean;
    sorter: (a: T, b: T) => number;
    /** How far down the ranking the pick may go; the whole list when omitted. */
    depth?: number;
    /** Of the ranked candidates, the ones close enough to the best to be picked at all. */
    near?: (r: T, best: T) => boolean;
    reason: (r: T) => Pick<SideSuggestion, 'reasonKey' | 'reasonParams' | 'foods'>;
  }

  const angles: Angle[] = [
    {
      key: 'fridge',
      pred: (r) => hitsOf(r) > 0,
      sorter: (a, b) => useOf(b).score - useOf(a).score || hitsOf(b) - hitsOf(a),
      depth: 3,
      // Variety among near-equals, never a side that uses clearly less of what is there.
      near: (r, best) => useOf(r).score >= useOf(best).score * NEAR_BEST,
      reason: (r) => {
        const foods = namedFoods(r);
        return foods.length > 0
          ? { reasonKey: 'sideReasons.fridgeFoods', foods }
          : { reasonKey: 'fanReasons.fridgeHits', reasonParams: { count: hitsOf(r) } };
      },
    },
    {
      key: 'together',
      pred: (r) => timeOf(r) !== null && timeOf(r)! <= togetherCap,
      sorter: byRating,
      depth: TOP_K,
      reason: (r) => ({ reasonKey: 'sideReasons.together', reasonParams: { count: timeOf(r)! } }),
    },
    {
      key: 'balance',
      pred: (r) => balanceKind !== undefined && sideKindOf(r) === balanceKind,
      sorter: byTime,
      depth: TOP_K,
      reason: () => ({ reasonKey: BALANCE_REASON[balanceKind ?? 'vegetable'] }),
    },
    {
      key: 'new',
      pred: (r) => !wasTried(r),
      sorter: byRating,
      reason: () => ({ reasonKey: 'fanReasons.new' }),
    },
  ];

  const rand = mulberry32(seed);
  const chosen = new Set<string>();
  const picks = new Map<SideAngleKey, T>();
  const candidatesOf = (angle: Angle) => pool.filter((r) => !chosen.has(r.id) && angle.pred(r));

  // Scarce angles pick first, so the one side that uses the fridge is not
  // taken by "ready together". Display order stays canonical below.
  [...angles]
    .sort((a, b) => pool.filter(a.pred).length - pool.filter(b.pred).length)
    .forEach((angle) => {
      const ranked = candidatesOf(angle).sort(angle.sorter);
      const candidates = angle.near && ranked.length > 0 ? ranked.filter((r) => angle.near!(r, ranked[0])) : ranked;
      if (candidates.length === 0) return;
      const pick = candidates[Math.floor(rand() * Math.min(angle.depth ?? candidates.length, candidates.length))];
      chosen.add(pick.id);
      picks.set(angle.key, pick);
    });

  return angles.flatMap((angle) => {
    const recipe = picks.get(angle.key);
    return recipe ? [{ angleKey: angle.key, recipe, ...angle.reason(recipe) }] : [];
  });
}

/** What the "balance" side brings, as a literal key per kind. */
const BALANCE_REASON: Record<SideKind, string> = {
  salad: 'sideReasons.fresh',
  vegetable: 'sideReasons.vegetable',
  starch: 'sideReasons.starch',
};
